import { Control, type ControlOptions } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import type { KeyEventArgs, MouseEventArgs, WheelEventArgs } from "../core/events.js";
import { clamp, type Point, type Rect, type Size } from "../../common/geometry.js";
import type { RenderContext, TooltipContent } from "../render/RenderContext.js";
import { drawCheckmark, drawTriangle } from "../themes/glyphs.js";
import type { Theme } from "../themes/Theme.js";
import {
  hasSubmenu,
  isSelectable,
  isSeparator,
  parseMnemonic,
  type MenuItemSpec,
} from "./MenuItem.js";

export interface MenuPopupOptions extends ControlOptions {
  items: MenuItemSpec[];
  /** The popup this one cascaded from, if any. */
  owner?: MenuPopup | null;
  /** Called after an item is invoked, so the whole chain can be torn down. */
  onDismiss?: () => void;
}

/**
 * How long the chosen row stays lit before the menu goes away.
 *
 * A mouse has been highlighting rows the whole way down the menu, so it knows
 * what it is about to pick. A finger arrives without ever hovering: without a
 * beat here the menu simply vanishes and nothing says which row was hit.
 */
const CHOICE_FLASH_MS = 120;
const SEPARATOR_HEIGHT = 7;
/**
 * Height of the strip at each end of a menu with more rows than it can show.
 *
 * Reserved out of the rows rather than floated over them: a row half under an
 * arrow is a row you cannot read and can still tap.
 */
const SCROLL_ARROW_HEIGHT = 16;
const CHECK_COLUMN = 22;
const ARROW_COLUMN = 18;
const SHORTCUT_GAP = 28;

/**
 * Nudge a menu of `size` whose preferred corner is `at` into the desktop's
 * work area.
 *
 * The work area, not the desktop: the strip a page reserves for its own
 * chrome — a title bar with a hamburger in it, a taskbar — is exactly the
 * part of the screen a menu must not cover, and it is already described by
 * `workAreaInsets`. Menus are the last thing that was still clamping to the
 * whole canvas.
 */
export function fitInWorkArea(desktop: Desktop, at: Point, size: Size): Point {
  const area = desktop.workArea;
  return {
    x: clamp(at.x, area.x, Math.max(area.x, area.x + area.width - size.width)),
    y: clamp(at.y, area.y, Math.max(area.y, area.y + area.height - size.height)),
  };
}

/**
 * A floating list of menu rows.
 *
 * Built from `MenuItemSpec` data and shown through the desktop's popup stack,
 * so a submenu is simply another popup pushed on top: the stack handles
 * dismissal, and closing a parent closes its children.
 */
export class MenuPopup extends Control {
  readonly items: MenuItemSpec[];
  readonly owner: MenuPopup | null;
  onDismiss: (() => void) | null;

  #highlight = -1;
  #submenu: MenuPopup | null = null;
  #openIndex = -1;
  /** The row the user picked, while it is lit and before it runs. */
  #chosen = -1;
  #chosenTimer: ReturnType<typeof setTimeout> | null = null;
  /** How far the rows have been scrolled up, in pixels. */
  #scroll = 0;

  constructor(options: MenuPopupOptions) {
    super({ width: 160, height: 40, tabStop: true, cursor: "default", ...options });
    this.items = options.items;
    this.owner = options.owner ?? null;
    this.onDismiss = options.onDismiss ?? null;

    // Whatever takes the menu down while a choice is lit, the choice still
    // runs: the user has committed to it, and dropping it silently would
    // spend the tap on nothing.
    this.on("closed", () => this.#commit());

    this.on("wheel", (e) => this.#onWheel(e));
    // A shorter menu shows fewer rows, so an offset that was legal a moment
    // ago may now be past the end.
    this.on("resize", () => this.clampScroll());

    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerleave", () => this.#onPointerLeave());
    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  /** Index of the highlighted row, or -1. */
  get highlightIndex(): number {
    return this.#highlight;
  }

  get openSubmenu(): MenuPopup | null {
    return this.#submenu;
  }

  /** The deepest popup in this chain. */
  get deepest(): MenuPopup {
    return this.#submenu ? this.#submenu.deepest : this;
  }

  // --- measurement ------------------------------------------------------

  rowHeight(theme: Theme): number {
    return Math.max(22, theme.metrics.listItemHeight);
  }

  /** Height of row `index`, accounting for separators. Zero if out of range. */
  itemHeight(index: number, theme: Theme): number {
    const item = this.items[index];
    if (!item) return 0;
    return isSeparator(item) ? SEPARATOR_HEIGHT : this.rowHeight(theme);
  }

  itemRect(index: number, theme: Theme): Rect {
    const view = this.viewportRect(theme);
    let y = view.y - this.#scroll;
    for (let i = 0; i < index; i++) y += this.itemHeight(i, theme);
    return { x: view.x, y, width: view.width, height: this.itemHeight(index, theme) };
  }

  private get padding(): number {
    return 3;
  }

  /** Row under a local point, or -1. Points in an arrow strip belong to no row. */
  indexAt(p: Point, theme: Theme): number {
    const view = this.viewportRect(theme);
    if (p.y < view.y || p.y >= view.y + view.height) return -1;
    let y = view.y - this.#scroll;
    for (let i = 0; i < this.items.length; i++) {
      const h = this.itemHeight(i, theme);
      if (p.y >= y && p.y < y + h) return i;
      y += h;
    }
    return -1;
  }

  // --- scrolling --------------------------------------------------------

  /** Total height of every row, whether or not there is room to show it. */
  contentHeight(theme: Theme): number {
    let h = 0;
    for (let i = 0; i < this.items.length; i++) h += this.itemHeight(i, theme);
    return h;
  }

  /**
   * Height of the arrow strip at each end, or 0 when every row fits.
   *
   * Measured against this menu's own bounds rather than the desktop: a menu
   * is only ever short because whoever opened it had less room than `measure`
   * asked for, and the bounds are where that decision ends up.
   */
  #arrowStrip(theme: Theme): number {
    return this.contentHeight(theme) + this.padding * 2 > this.height ? SCROLL_ARROW_HEIGHT : 0;
  }

  /** The band the rows are drawn in, inside the padding and the arrows. */
  viewportRect(theme: Theme): Rect {
    const strip = this.#arrowStrip(theme);
    return {
      x: this.padding,
      y: this.padding + strip,
      width: Math.max(0, this.width - this.padding * 2),
      height: Math.max(0, this.height - this.padding * 2 - strip * 2),
    };
  }

  /** How far the rows can be scrolled. Zero when they all fit. */
  maxScroll(theme: Theme): number {
    return Math.max(0, this.contentHeight(theme) - this.viewportRect(theme).height);
  }

  /** True when the menu has more rows than it can show at once. */
  scrolls(theme: Theme): boolean {
    return this.maxScroll(theme) > 0;
  }

  /** How far the rows have been scrolled up. */
  get scrollOffset(): number {
    return this.#scroll;
  }

  /**
   * Scroll to `offset`, clamped to the ends.
   *
   * The theme is passed in rather than read off the desktop, as it is for
   * every other measurement here: row height is a theme metric, and a popup
   * that has not been shown yet has no desktop to ask.
   */
  scrollTo(offset: number, theme: Theme): void {
    const next = clamp(offset, 0, this.maxScroll(theme));
    if (next === this.#scroll) return;
    this.#scroll = next;
    this.invalidate();
  }

  /** Pull the offset back into range after the menu or its rows changed. */
  clampScroll(theme?: Theme): void {
    const t = theme ?? this.desktop?.theme;
    if (t) this.scrollTo(this.#scroll, t);
  }

  /** Bring row `index` wholly into view, moving as little as possible. */
  scrollIntoView(index: number, theme: Theme): void {
    if (index < 0 || index >= this.items.length) return;
    const view = this.viewportRect(theme);
    if (view.height <= 0) return;
    let top = 0;
    for (let i = 0; i < index; i++) top += this.itemHeight(i, theme);
    const bottom = top + this.itemHeight(index, theme);
    if (top < this.#scroll) this.scrollTo(top, theme);
    else if (bottom > this.#scroll + view.height) this.scrollTo(bottom - view.height, theme);
  }

  /**
   * One-finger and two-finger scrolling.
   *
   * Implementing this is the whole of what makes a long menu draggable on a
   * phone: the input layer holds a touch press back until it has moved far
   * enough to be a scroll, offers the movement here, and only lets the press
   * through as a tap when nothing takes it.
   */
  override panBy(_dx: number, dy: number): Point {
    const theme = this.desktop?.theme;
    if (!theme || dy === 0) return { x: 0, y: 0 };
    const before = this.#scroll;
    this.scrollTo(before + dy, theme);
    return { x: 0, y: this.#scroll - before };
  }

  /** Which arrow strip a local point is in: -1 for up, 1 for down, 0 for neither. */
  #arrowAt(p: Point, theme: Theme): number {
    const strip = this.#arrowStrip(theme);
    if (strip === 0) return 0;
    if (p.y < this.padding + strip) return -1;
    if (p.y >= this.height - this.padding - strip) return 1;
    return 0;
  }

  /** A page is everything showing bar one row, so the eye keeps its place. */
  #pageStep(theme: Theme): number {
    return Math.max(this.rowHeight(theme), this.viewportRect(theme).height - this.rowHeight(theme));
  }

  #onWheel(e: WheelEventArgs): void {
    const theme = this.desktop?.theme;
    if (!theme) return;
    const before = this.#scroll;
    this.scrollTo(before + e.deltaY, theme);
    if (this.#scroll !== before) e.handled = true;
  }

  /**
   * Size the popup to its content using the given theme's fonts.
   *
   * `maxHeight` is the room the caller actually has — the desktop's work
   * area, usually. A menu with more rows than that comes back capped, and
   * scrolls the rest.
   */
  measure(rc: RenderContext, theme: Theme, maxHeight?: number): Size {
    const font = theme.metrics.font;
    let text = 0;
    let shortcut = 0;
    let height = this.padding * 2;
    let anySubmenu = false;
    for (const item of this.items) {
      if (isSeparator(item)) {
        height += SEPARATOR_HEIGHT;
        continue;
      }
      height += this.rowHeight(theme);
      const label = parseMnemonic(item.text ?? "");
      text = Math.max(text, rc.measureText(label.text, font).width);
      if (item.shortcut) shortcut = Math.max(shortcut, rc.measureText(item.shortcut, font).width);
      if (hasSubmenu(item)) anySubmenu = true;
    }
    const width =
      this.padding * 2 +
      CHECK_COLUMN +
      Math.ceil(text) +
      (shortcut > 0 ? SHORTCUT_GAP + Math.ceil(shortcut) : 0) +
      (anySubmenu ? ARROW_COLUMN : 8) +
      8;
    const capped = maxHeight === undefined ? height : Math.min(height, Math.max(0, maxHeight));
    return { width: Math.max(120, width), height: capped };
  }

  /** `measure`, capped to the room a desktop's work area leaves for a menu. */
  measureIn(desktop: Desktop): Size {
    return this.measure(desktop.rc, desktop.theme, desktop.workArea.height);
  }

  // --- highlight and submenus -------------------------------------------

  setHighlight(index: number): void {
    // A lit choice is the answer to "which row?" — nothing may move it.
    if (this.#chosen >= 0) return;
    if (this.#highlight === index) return;
    this.#highlight = index;
    const theme = this.desktop?.theme;
    if (theme) this.scrollIntoView(index, theme);
    this.invalidate();
  }

  /** Move the highlight by `delta`, skipping separators and disabled rows. */
  moveHighlight(delta: number): void {
    const n = this.items.length;
    if (n === 0) return;
    let i = this.#highlight;
    for (let step = 0; step < n; step++) {
      i = (((i + delta) % n) + n) % n;
      if (isSelectable(this.items[i]!)) {
        this.setHighlight(i);
        return;
      }
    }
  }

  #firstSelectable(from: number, delta: number): number {
    const n = this.items.length;
    for (let step = 0; step < n; step++) {
      const i = (((from + step * delta) % n) + n) % n;
      if (isSelectable(this.items[i]!)) return i;
    }
    return -1;
  }

  /** Open the submenu of row `index` beside this popup. */
  openSubmenuAt(index: number): MenuPopup | null {
    const desktop = this.desktop;
    const item = this.items[index];
    if (!desktop || !item || !hasSubmenu(item) || !isSelectable(item)) return null;
    if (this.#openIndex === index && this.#submenu) return this.#submenu;

    this.closeSubmenu();
    const theme = desktop.theme;
    const child = new MenuPopup({
      items: item.items!,
      owner: this,
      onDismiss: this.onDismiss ?? undefined,
    });
    const size = child.measureIn(desktop);
    const row = this.itemRect(index, theme);
    const anchor = this.screenBounds;
    const area = desktop.workArea;

    // Prefer the right-hand side; flip left when it would leave the work area.
    let x = anchor.x + anchor.width - 2;
    if (x + size.width > area.x + area.width) x = anchor.x - size.width + 2;
    const placed = fitInWorkArea(desktop, { x, y: anchor.y + row.y - this.padding }, size);

    child.setBounds(placed.x, placed.y, size.width, size.height);
    child.on("closed", () => {
      if (this.#submenu === child) {
        this.#submenu = null;
        this.#openIndex = -1;
      }
    });
    this.#submenu = child;
    this.#openIndex = index;
    desktop.showPopup(child, { nested: true });
    return child;
  }

  closeSubmenu(): void {
    const child = this.#submenu;
    if (!child) return;
    this.#submenu = null;
    this.#openIndex = -1;
    child.closeSubmenu();
    this.desktop?.closePopup(child);
  }

  /** True while a chosen row is lit and the menu is about to close. */
  get choosing(): boolean {
    return this.#chosen >= 0;
  }

  /**
   * The user picked row `index`.
   *
   * Where `activate` runs a row at once, this lights it first and holds it for
   * a beat, so what was picked is visible before the menu goes. A submenu row
   * opens straight away: the submenu appearing is its own answer.
   */
  choose(index: number): void {
    if (this.#chosen >= 0) return; // one choice per menu
    const item = this.items[index];
    if (!item || !isSelectable(item)) return;
    if (hasSubmenu(item)) {
      this.activate(index);
      return;
    }
    this.setHighlight(index);
    this.#chosen = index;
    this.#chosenTimer = setTimeout(() => {
      this.#chosenTimer = null;
      this.#commit();
    }, CHOICE_FLASH_MS);
  }

  /** Run the lit choice, once, however the wait ended. */
  #commit(): void {
    const index = this.#chosen;
    if (index < 0) return;
    this.#chosen = -1;
    if (this.#chosenTimer !== null) {
      clearTimeout(this.#chosenTimer);
      this.#chosenTimer = null;
    }
    this.activate(index);
  }

  /** Run a row: invoke a leaf and dismiss, or open its submenu. */
  activate(index: number): void {
    const item = this.items[index];
    if (!item || !isSelectable(item)) return;
    if (hasSubmenu(item)) {
      const child = this.openSubmenuAt(index);
      child?.moveHighlight(1);
      child?.focus();
      return;
    }
    const dismiss = this.onDismiss;
    this.desktop?.closeAllPopups();
    dismiss?.();
    item.onClick?.(item);
  }

  /** Jump to the next row whose mnemonic is `char`; activate a unique match. */
  handleMnemonic(char: string): boolean {
    const key = char.toLowerCase();
    const matches: number[] = [];
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i]!;
      if (!isSelectable(item)) continue;
      if (parseMnemonic(item.text ?? "").mnemonic === key) matches.push(i);
    }
    if (matches.length === 0) return false;
    if (matches.length === 1) {
      this.choose(matches[0]!);
      return true;
    }
    // Several rows share the letter: cycle rather than pick one arbitrarily.
    const after = matches.find((i) => i > this.#highlight);
    this.setHighlight(after ?? matches[0]!);
    return true;
  }

  // --- input ------------------------------------------------------------

  protected override hitTestSelf(): boolean {
    return true;
  }

  /** The row under the pointer says what it does, if it was given anything to say. */
  override getTooltip(p: Point): TooltipContent | null {
    const theme = this.desktop?.theme;
    if (!theme) return this.tooltip;
    const item = this.items[this.indexAt(p, theme)];
    return item && !isSeparator(item) ? (item.tooltip ?? null) : this.tooltip;
  }

  #onPointerMove(e: MouseEventArgs): void {
    const theme = this.desktop?.theme;
    if (!theme) return;
    const index = this.indexAt({ x: e.x, y: e.y }, theme);
    if (index === this.#highlight) return;
    this.setHighlight(index);

    const item = index >= 0 ? this.items[index] : undefined;
    if (item && isSelectable(item) && hasSubmenu(item)) {
      // Hovering a parent row opens it, as Windows does after a short pause.
      this.openSubmenuAt(index);
    } else if (this.#submenu) {
      // Moving onto any other row abandons the open submenu.
      this.closeSubmenu();
    }
  }

  #onPointerLeave(): void {
    // Keep the highlight while a submenu is open: the pointer is on its way
    // there, and clearing it would make the parent row look inactive.
    if (this.#submenu) return;
    this.setHighlight(-1);
  }

  #onPointerDown(e: MouseEventArgs): void {
    const theme = this.desktop?.theme;
    if (!theme) return;
    e.handled = true;
    const arrow = this.#arrowAt({ x: e.x, y: e.y }, theme);
    if (arrow !== 0) {
      this.scrollTo(this.#scroll + arrow * this.#pageStep(theme), theme);
      return;
    }
    const index = this.indexAt({ x: e.x, y: e.y }, theme);
    if (index < 0) return;
    this.choose(index);
  }

  #onKeyDown(e: KeyEventArgs): void {
    switch (e.key) {
      case "ArrowDown":
        this.moveHighlight(1);
        e.handled = true;
        return;
      case "ArrowUp":
        this.moveHighlight(-1);
        e.handled = true;
        return;
      case "PageDown":
      case "PageUp": {
        const theme = this.desktop?.theme;
        if (theme) this.scrollTo(this.#scroll + (e.key === "PageUp" ? -1 : 1) * this.#pageStep(theme), theme);
        e.handled = true;
        return;
      }
      case "Home":
        this.setHighlight(this.#firstSelectable(0, 1));
        e.handled = true;
        return;
      case "End":
        this.setHighlight(this.#firstSelectable(this.items.length - 1, -1));
        e.handled = true;
        return;
      case "ArrowRight": {
        const item = this.items[this.#highlight];
        if (item && hasSubmenu(item) && isSelectable(item)) {
          this.activate(this.#highlight);
          e.handled = true;
        }
        return;
      }
      case "ArrowLeft":
        // Back out to the parent menu, leaving it open.
        if (this.owner) {
          this.owner.closeSubmenu();
          this.owner.focus();
          e.handled = true;
        }
        return;
      case "Enter":
      case " ":
        if (this.#highlight >= 0) this.choose(this.#highlight);
        e.handled = true;
        return;
      case "Escape": {
        const owner = this.owner;
        this.desktop?.closePopup(this);
        if (owner) {
          owner.closeSubmenu();
          owner.focus();
        } else {
          this.onDismiss?.();
        }
        e.handled = true;
        return;
      }
      default:
        break;
    }
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && this.handleMnemonic(e.key)) {
      e.handled = true;
    }
  }

  // --- painting ---------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    theme.drawPanel(rc, this.localRect, false);
    // Rows are clipped to the band between the arrows, so the one straddling
    // either end is cut rather than drawn over the panel's border.
    rc.clipped(this.viewportRect(theme), () => this.#paintRows(rc, theme));
    this.#paintScrollArrows(rc, theme);
  }

  /** The up and down strips, lit only in the direction there is more to see. */
  #paintScrollArrows(rc: RenderContext, theme: Theme): void {
    const strip = this.#arrowStrip(theme);
    if (strip === 0) return;
    const p = theme.palette;
    const max = this.maxScroll(theme);
    const width = this.width - this.padding * 2;
    const up: Rect = { x: this.padding, y: this.padding, width, height: strip };
    const down: Rect = { ...up, y: this.height - this.padding - strip };
    drawTriangle(rc, up, "up", this.#scroll > 0 ? p.text : p.textDisabled, 4);
    drawTriangle(rc, down, "down", this.#scroll < max ? p.text : p.textDisabled, 4);
  }

  #paintRows(rc: RenderContext, theme: Theme): void {
    const p = theme.palette;
    const font = theme.metrics.font;
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i]!;
      const r = this.itemRect(i, theme);

      if (isSeparator(item)) {
        rc.hLine(r.x + CHECK_COLUMN, Math.round(r.y + r.height / 2), r.width - CHECK_COLUMN - 6, p.gridLine);
        continue;
      }

      const enabled = isSelectable(item);
      const highlighted = i === this.#highlight && enabled;
      if (highlighted) {
        theme.drawListItem(rc, r, {
          hovered: true,
          pressed: false,
          focused: false,
          disabled: false,
          selected: true,
          active: true,
        });
      }
      const ink = !enabled
        ? p.textDisabled
        : highlighted
          ? theme.listItemTextColor({
              hovered: true,
              pressed: false,
              focused: false,
              disabled: false,
              selected: true,
              active: true,
            })
          : p.text;

      // Check column: a tick, a bullet, or an icon.
      if (item.checked) {
        const box: Rect = { x: r.x + 2, y: r.y + 2, width: CHECK_COLUMN - 6, height: r.height - 4 };
        if (item.radio) {
          const d = 6;
          rc.fillRect(
            { x: box.x + (box.width - d) / 2, y: box.y + (box.height - d) / 2, width: d, height: d },
            ink,
            d / 2,
          );
        } else {
          drawCheckmark(rc, box, ink);
        }
      } else if (item.icon) {
        const size = Math.min(16, r.height - 6);
        rc.drawTexture(item.icon, {
          x: r.x + 3,
          y: r.y + (r.height - size) / 2,
          width: size,
          height: size,
        });
      }

      const label = parseMnemonic(item.text ?? "");
      const textX = r.x + CHECK_COLUMN;
      const midY = r.y + r.height / 2;
      rc.drawText(label.text, textX, midY, font, ink, { baseline: "middle" });

      // Underline the mnemonic character.
      if (label.mnemonicIndex >= 0) {
        const before = rc.measureText(label.text.slice(0, label.mnemonicIndex), font).width;
        const w = rc.measureText(label.text[label.mnemonicIndex]!, font).width;
        const m = rc.measureText(label.text, font);
        rc.hLine(textX + before, Math.round(midY + m.height / 2), Math.ceil(w), ink);
      }

      if (item.shortcut) {
        const sw = rc.measureText(item.shortcut, font).width;
        rc.drawText(
          item.shortcut,
          r.x + r.width - ARROW_COLUMN - sw,
          midY,
          font,
          enabled ? (highlighted ? ink : p.textSubtle) : p.textDisabled,
          { baseline: "middle" },
        );
      }

      if (hasSubmenu(item)) {
        drawTriangle(
          rc,
          { x: r.x + r.width - ARROW_COLUMN, y: r.y, width: ARROW_COLUMN, height: r.height },
          "right",
          ink,
          4,
        );
      }
    }
  }
}
